import { Toast } from "@base-ui/react/toast";
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import "../tokens.stylex.ts";
import { color, font, fontSize, fontWeight, lineHeight, media, motion, radius, space, texture, zIndex } from "../tokens.stylex.ts";
import { AlertIcon, CloseIcon, InfoIcon, SuccessIcon } from "../icons.ts";
import { isTransitioning } from "./overlay.styles.ts";
import { surface } from "./surface.styles.ts";
const manager = Toast.createToastManager();

/** Imperative API usable anywhere (mutations, event handlers). */
export const toast = {
  success: (title: string, description?: string) => manager.add({
    title,
    description,
    type: "success"
  }),
  error: (title: string, description?: string) => manager.add({
    title,
    description,
    type: "error",
    priority: "high",
    timeout: 8000
  }),
  info: (title: string, description?: string) => manager.add({
    title,
    description,
    type: "info"
  })
};
export function ToastProvider({
  children
}: {
  children: ReactNode;
}) {
  return <Toast.Provider toastManager={manager} limit={3}>
      {children}
      <Toast.Portal>
        <Toast.Viewport className="xixxii4 xsyyyt5 x138g1s x1eq01xp xcxjshg x2n2wy4 x16htxqe x1q8nuce x78zum5 xdt5ytf x1g0459a x1a62zme x11f3qql xyrwpcj x1a2a7pz">
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>;
}
function ToastList() {
  const {
    toasts
  } = Toast.useToastManager();
  return toasts.map(item => <Toast.Root key={item.id} toast={item} className={state => stylex.props(surface.raised, surface.md, styles.toast, faceTones[toneOf(item.type)], isTransitioning(state.transitionStatus) && styles.hidden).className}>
      <span {...stylex.props(styles.icon, iconTones[toneOf(item.type)])}>
        {item.type === "error" ? <AlertIcon size={15} /> : item.type === "success" ? <SuccessIcon size={15} /> : <InfoIcon size={15} />}
      </span>
      <div className="x78zum5 xdt5ytf x195vfkc x1iyjqo2 xeuugli">
        <Toast.Title className="x1ghz6dp xyqdw3p xluwven x1219144 xlt6b7" />
        <Toast.Description className="x1ghz6dp xxg9qwl xmccgnw xq8kp7w x9tub0y" />
      </div>
      <Toast.Close aria-label="Dismiss" className="x78zum5 x1i3ajwb x1x1umut x1tkwxaq xc342km x1y8ptio xjbqb8w x1i7ng9n x1gullwp x1ypdohk">
        <CloseIcon size={14} />
      </Toast.Close>
    </Toast.Root>);
}
function toneOf(type: string | undefined): "success" | "error" | "info" {
  return type === "error" || type === "success" ? type : "info";
}
const styles = {
  toast: {
    "--toast-title": "xyobkcc",
    "--toast-description": "xme9sg5",
    "--toast-close": "x1u3zhka",
    "--toast-close-hover": "x19iudub",
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xpcbtbh",
    k8WAf4: "xk7tk9l",
    kg3NbH: "xsef1i0",
    kaIpWk: "xlfxrz8",
    k1ekBW: "x6o7n8i",
    kIyJzY: "xjx9puj",
    kAMwcw: "xyda15a",
    $$css: true
  },
  hidden: {
    kSiTet: "xg01cxk",
    k3aq6I: "x1so2ola x4ylgna",
    $$css: true
  },
  icon: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kzqmXN: "x23j0i4",
    kZKoxP: "xd7y6wv",
    kaIpWk: "xoazux2",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    $$css: true
  }
};

// Same recipe as Button's success/error feedback: a solid status face resting
// on its opaque `Soft` slab.
const faceTones = {
  success: {
    "--face-dot": "x1tfdxok",
    "--slab-hatch": "x100daw0",
    "--toast-title": "x1f2uyj8",
    "--toast-description": "xfvbk0w",
    "--toast-close": "x1a1wvob",
    "--toast-close-hover": "x1khxqxx",
    "--face-bg": "xpvalor",
    "--face-border": "x1tduo4w",
    "--slab-bg": "xna4wdy",
    "--slab-border": "x1dcnwlw",
    $$css: true
  },
  error: {
    "--face-dot": "x1tfdxok",
    "--slab-hatch": "x100daw0",
    "--toast-title": "x1f2uyj8",
    "--toast-description": "xfvbk0w",
    "--toast-close": "x1a1wvob",
    "--toast-close-hover": "x1khxqxx",
    "--face-bg": "x1j6b88p",
    "--face-border": "x1rkrvrg",
    "--slab-bg": "x1gpgqo3",
    "--slab-border": "xdr542g",
    $$css: true
  },
  info: {
    $$css: true
  }
};
const iconTones = {
  success: {
    kMwMTN: "x14f1h33",
    kWkggS: "x1u2fupe",
    kVAM5u: "xx3vu7j",
    $$css: true
  },
  error: {
    kMwMTN: "x14f1h33",
    kWkggS: "x1u2fupe",
    kVAM5u: "xx3vu7j",
    $$css: true
  },
  info: {
    kMwMTN: "x187ovcu",
    kWkggS: "xyez3s8",
    kVAM5u: "x4bnxre",
    $$css: true
  }
};